import { AlertTriangle, ShieldX, Terminal } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

/**
 * The three error-free states are unchanged. What matters here is that a
 * `technical` authError is NOT dressed up as a rejection: this screen used to
 * announce "Access Denied — restricted to @finn.com accounts" for any failure,
 * including a blocked pop-up, so users with a working account were told to stop
 * trying. Keep the two kinds visually distinct — red shield for "not allowed",
 * amber warning for "broke, try this".
 */
export default function LoginView() {
    const { signIn, isConfigured, authError } = useAuth();
    const isDenied = authError?.kind === 'domain';
    const isBroken = authError?.kind === 'technical';

    return (
        <div className="min-h-screen bg-black text-zinc-200 flex flex-col items-center justify-center p-6">
            <div className="w-full max-w-md bg-zinc-900/50 backdrop-blur-xl border border-zinc-800 rounded-3xl p-8 shadow-2xl">
                <div className="text-center space-y-6">
                    <div className="bg-black rounded-lg mx-auto w-fit flex items-center justify-center shadow-lg border border-[#0072EA] px-3 py-1.5">
                        {isDenied ? (
                            <ShieldX className="w-8 h-8 text-red-400" />
                        ) : isBroken ? (
                            <AlertTriangle className="w-8 h-8 text-amber-400" />
                        ) : isConfigured ? (
                            <span className="text-white font-bold text-2xl tracking-tight">FINN</span>
                        ) : (
                            <Terminal className="w-8 h-8 text-yellow-500" />
                        )}
                    </div>

                    <div className="space-y-2">
                        <h1 className="text-2xl font-bold text-white tracking-tight">
                            {isDenied
                                ? 'Access Denied'
                                : isBroken
                                ? "Sign-in didn't complete"
                                : isConfigured
                                ? 'People Space'
                                : 'Setup Mode'}
                        </h1>
                        <p className="text-zinc-400 text-sm">
                            {isDenied
                                ? 'This application is restricted to @finn.com accounts.'
                                : isBroken
                                ? 'Your account is fine — the sign-in window did not finish.'
                                : isConfigured
                                ? 'Sign in with your FINN Google account.'
                                : 'Application is running without cloud configuration.'}
                        </p>
                    </div>

                    {authError && (
                        <div
                            className={`rounded-xl p-4 text-left ${
                                isDenied
                                    ? 'bg-red-500/10 border border-red-500/20'
                                    : 'bg-amber-500/10 border border-amber-500/20'
                            }`}
                        >
                            <p
                                className={`text-xs font-medium ${
                                    isDenied ? 'text-red-400' : 'text-amber-400'
                                }`}
                            >
                                {isDenied ? 'Access Restricted' : 'What to try'}
                            </p>
                            <p
                                className={`text-[10px] leading-relaxed mt-1 ${
                                    isDenied ? 'text-red-400/80' : 'text-amber-400/80'
                                }`}
                            >
                                {authError.message}
                            </p>
                            {/* The code is what makes a user's screenshot diagnosable. */}
                            {authError.code && (
                                <p className="text-zinc-500 text-[10px] leading-relaxed mt-2 font-mono">
                                    {authError.code}
                                </p>
                            )}
                        </div>
                    )}

                    <div className="pt-4">
                        <button
                            onClick={signIn}
                            disabled={!isConfigured}
                            className="w-full flex items-center justify-center gap-3 bg-white text-black px-6 py-3.5 rounded-xl font-medium hover:bg-zinc-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isConfigured ? (
                                <>
                                    <img src="https://www.google.com/favicon.ico" alt="Google" className="w-5 h-5" />
                                    <span>
                                        {isDenied
                                            ? 'Try Again with FINN Account'
                                            : isBroken
                                            ? 'Try Again'
                                            : 'Continue with Google'}
                                    </span>
                                </>
                            ) : (
                                <span className="text-zinc-500">Configuration Required</span>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
